import { BookOpen, Cpu, HeartPulse, Landmark, Waves } from "lucide-react";
import { DossierLayout } from "@/components/DossierLayout";
import { IMAGES } from "@/data/content";

const DESKS = [
  {
    id: "climate",
    icon: Waves,
    title: "Climate and Environmental Resilience",
    questions: [
      "How are coastal communities adapting to stronger storms, flooding, erosion and heat?",
      "Are adaptation plans producing visible protection at community level?",
      "How resilient are hospitals, schools, roads, water systems and housing?",
      "Who receives climate finance, and how is it tracked?",
      "What can Ghana and Jamaica learn from each other's disaster-management experience?",
    ],
    concepts: [
      "After the Warning",
      "Different Shores, the Same Rising Water",
      "The Climate Audit",
      "When the Hospital Becomes a Disaster Zone",
      "Heat Without Headlines",
    ],
  },
  {
    id: "health",
    icon: HeartPulse,
    title: "Health and Human Development",
    questions: [
      "How is Jamaica reforming primary healthcare?",
      "What is the impact of migration among nurses, doctors and care professionals?",
      "How are noncommunicable diseases affecting families and public expenditure?",
      "How prepared is the health system for dengue, extreme heat and climate emergencies?",
      "Where can Jamaica, Ghana and Canada collaborate on care training and workforce retention?",
    ],
    concepts: [
      "Who Cares for the Country That Trains the World's Carers?",
      "The Care Drain",
      "Climate in the Clinic",
      "The Primary-Care Promise",
      "The Invisible Workforce Holding Families Together",
    ],
  },
  {
    id: "governance",
    icon: Landmark,
    title: "Governance and Democratic Institutions",
    questions: [
      "How are national plans monitored and translated into local outcomes?",
      "How can citizens track promises, budgets and delivery?",
      "How do violence and organized crime affect public trust and institutional capacity?",
      "What can Ghana and Jamaica learn from each other's accountability systems?",
    ],
    concepts: [
      "Vision Without Delivery",
      "The Accountability Gap",
      "The Cost of Fear",
      "Auditing Resilience",
      "Two Democracies, One Question",
    ],
  },
  {
    id: "culture",
    icon: BookOpen,
    title: "Culture, Identity and History",
    questions: [
      "How can Ghana–Jamaica relations move beyond symbolism?",
      "How do language, music, religion and memory preserve African continuities?",
      "What would meaningful reparatory justice look like?",
      "How can heritage tourism avoid reducing history to performance?",
    ],
    concepts: [
      "Ghana and Jamaica Have Found Each Other Again",
      "Coming Home to a Place You Have Never Lived",
      "Beyond the Door of No Return",
      "What Would Repair Actually Look Like?",
      "The Archive in the Music",
    ],
  },
  {
    id: "innovation",
    icon: Cpu,
    title: "Innovation, AI and Economic Transformation",
    questions: [
      "Can Jamaica convert cultural capital into technology, film, publishing, education and digital exports?",
      "How can AI strengthen agriculture, health, disaster response and accountability without worsening inequality?",
      "What digital vulnerabilities threaten small-island states?",
      "What role can the diaspora play in investment and mentorship?",
    ],
    concepts: [
      "The Patois Protocol",
      "From Cultural Power to Economic Power",
      "Can AI Help Feed the Caribbean?",
      "The Digital Island",
      "The Diaspora Dividend",
    ],
  },
];

const DATA = {
  country: "Jamaica",
  region: "Caribbean",
  subtitle:
    "A living research and editorial asset examining Jamaica as a strategic node connecting Africa, the Caribbean, North America, the United Kingdom and the global African diaspora.",
  image: IMAGES.kingstonCity,
  imageAlt: "Aerial view of Kingston, Jamaica",
  brief: {
    title: "The Launch Point for a Wider Caribbean Knowledge Programme",
    copy: "Jamaica is the Atlantic Bridge Institute's first full country dossier and the launch point for a wider Caribbean knowledge programme. The dossier supports rigorous journalism, policy analysis, documentary concepts, podcast development, books and partnerships.",
  },
  contextTitle: "Four Long-Term National Goals",
  contextCopy: "Jamaica's national development agenda is presented around four long-term goals.",
  goals: [
    "Empowering Jamaicans to achieve their fullest potential",
    "Building a secure, cohesive and just society",
    "Creating a prosperous economy",
    "Maintaining a healthy natural environment",
  ],
  factors: [
    "Hurricanes, flooding, drought, heat and sea-level rise",
    "Dependence on tourism and agriculture",
    "Pressure on health systems and health-worker capacity",
    "Food-import dependence",
    "The need to convert cultural influence into broader economic, knowledge and technological power",
  ],
  desks: DESKS,
  projectsTitle: "The First Jamaica Slate",
  projectsCopy: "The ten projects below form the dossier's opening journalism programme.",
  projects: [
    "Ghana and Jamaica Have Found Each Other Again—What Happens Next?",
    "Different Shores, the Same Rising Water",
    "Who Cares for the Country That Trains the World's Carers?",
    "Paradise Cannot Feed Itself",
    "What Would Repair Actually Look Like?",
    "Coming Home to a Place You Have Never Lived",
    "Vision Without Delivery",
    "The Patois Protocol",
    "The Diaspora Dividend",
    "The Climate Audit",
  ],
  stakeholders: [
    {
      group: "Jamaica",
      items: [
        "Planning Institute of Jamaica",
        "Ministry responsible for foreign affairs and foreign trade",
        "Ministry responsible for health and wellness",
        "Ministry responsible for environment and climate change",
        "Office of Disaster Preparedness and Emergency Management",
        "Auditor General's Department",
        "University of the West Indies, Mona",
        "University of Technology, Jamaica",
        "Caribbean Institute for Media and Communication",
        "Jamaica Information Service",
      ],
    },
    {
      group: "Regional and International",
      items: [
        "CARICOM Secretariat",
        "Caribbean Public Health Agency",
        "Caribbean Disaster Emergency Management Agency",
        "Pan American Health Organization",
        "World Bank",
        "Inter-American Development Bank",
        "United Nations agencies",
        "African Union and diaspora institutions",
        "Universities and research centres in Ghana and Canada",
      ],
    },
  ],
  sources: [
    "Vision 2030 Jamaica",
    "Planning Institute of Jamaica",
    "Jamaica Information Service",
    "Auditor General's Department",
    "PAHO Jamaica",
    "World Bank Jamaica data",
    "CARICOM initiatives",
    "Parliamentary records",
    "Ministry reports",
    "National statistics",
  ],
  plan: [
    { phase: "Days 1–30", title: "Build the Evidence Base" },
    { phase: "Days 31–60", title: "Publish and Convene" },
    { phase: "Days 61–90", title: "Institutionalize" },
  ],
  measures: [
    { figure: "12", label: "Major analytical or reported articles" },
    { figure: "12", label: "Podcast episodes" },
    { figure: "4", label: "Policy briefs" },
    { figure: "1", label: "Documentary treatment" },
    { figure: "1", label: "Book proposal" },
    { figure: "25", label: "Active institutional relationships" },
    { figure: "Verified", label: "Source and expert database" },
    { figure: "Growth", label: "Audience across Ghana, Jamaica, Canada and the wider diaspora" },
  ],
  downloads: [
    { title: "Jamaica Country Dossier", note: "PDF · In development", button: "Download Dossier PDF — Coming Soon", testid: "dossier-download-pdf-button" },
    { title: "Ghana–Jamaica Cooperation Tracker", note: "PDF · In development", button: "Download — Coming Soon", testid: "dossier-download-tracker-button" },
  ],
  latestStory: {
    label: "Ghana–Jamaica Relations",
    title: "From Barrier to Bridge: What Ghana and Jamaica Must Build After the Presidential Visit",
    to: "/analysis/ghana-jamaica-presidential-visit",
  },
  editorialStandard:
    "Every output must be evidence-based, human-centred, comparative where useful, solutions-oriented and respectful of local agency. Atlantic Bridge will not treat Jamaica as a backdrop for outside interpretation. Jamaican scholars, journalists, practitioners and communities must be central to the work.",
  cta: {
    kicker: "Country Dossiers",
    title: "More Dossiers in Development",
    copy: "Ghana is in active development; Barbados, Trinidad and Tobago, Guyana and Nigeria follow as research is completed and verified.",
    buttonLabel: "Explore All Countries",
    to: "/countries",
  },
};

export default function JamaicaDossierPage() {
  return <DossierLayout data={DATA} />;
}
